import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode
} from 'react'
import { useQueryClient } from '@tanstack/react-query'

import {
  clearStoredAuthToken,
  fetchCurrentUser,
  getStoredAuthToken,
  login,
  logout,
  storeAuthToken,
  type AuthUser
} from './api'

type AuthState =
  | { status: 'loading'; user: null }
  | { status: 'anonymous'; user: null }
  | { status: 'authenticated'; user: AuthUser }

type AuthContextValue = AuthState & {
  signIn: (email: string, password: string) => Promise<void>
  signOut: () => Promise<void>
}

const AuthContext = createContext<AuthContextValue | null>(null)

export function AuthProvider({ children }: { children: ReactNode }) {
  const [state, setState] = useState<AuthState>({ status: 'loading', user: null })
  const queryClient = useQueryClient()

  useEffect(() => {
    const token = getStoredAuthToken()
    if (!token) {
      setState({ status: 'anonymous', user: null })
      return
    }
    let cancelled = false
    const restoreSession = async () => {
      try {
        const user = await fetchCurrentUser()
        if (!cancelled) {
          setState({ status: 'authenticated', user })
        }
      } catch {
        clearStoredAuthToken()
        if (!cancelled) {
          setState({ status: 'anonymous', user: null })
        }
      }
    }
    void restoreSession()
    return () => {
      cancelled = true
    }
  }, [])

  const signIn = useCallback(async (email: string, password: string) => {
    const session = await login({ email, password })
    storeAuthToken(session.access_token)
    queryClient.clear()
    setState({ status: 'authenticated', user: session.user })
  }, [queryClient])

  const signOut = useCallback(async () => {
    try {
      await logout()
    } finally {
      clearStoredAuthToken()
      queryClient.clear()
      setState({ status: 'anonymous', user: null })
    }
  }, [queryClient])

  const value = useMemo<AuthContextValue>(
    () => ({
      ...state,
      signIn,
      signOut
    }),
    [signIn, signOut, state]
  )

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>
}

export function useAuth(): AuthContextValue {
  const value = useContext(AuthContext)
  if (!value) {
    throw new Error('useAuth must be used inside AuthProvider')
  }
  return value
}
